import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { IconX } from "@tabler/icons";
import { count } from "./format";

// Layout pieces: Tabs (+ TabPanel), Drawer (right side sheet), Toolbar,
// Stack and FieldGrid (flex / grid helpers so pages need no bootstrap grid).

/* ------------------------------------------------------------------ */
/* Tabs                                                                */
/* ------------------------------------------------------------------ */

// Underlined page-level tabs. items: [{ key, label, count?, icon?, disabled? }].
// Controlled: value + onChange(key). Arrow keys / Home / End move between tabs.
// Pair with <TabPanel idPrefix tab value> for the aria wiring.
export function Tabs({ items, value, onChange, ariaLabel = "Sections", idPrefix = "pa-tabs", className = "" }) {
  const refs = useRef([]);
  const enabled = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);
  // The tab in the page's tab order: the active one, or the first enabled tab
  // while value matches no enabled tab (undefined, or a stale saved key).
  const activeIndex = items.findIndex((it) => it.key === value && !it.disabled);
  const tabStop = activeIndex >= 0 ? activeIndex : enabled.length ? enabled[0] : -1;
  const go = (i) => {
    const it = items[i];
    if (!it) return;
    onChange(it.key);
    if (refs.current[i]) refs.current[i].focus();
  };
  const onKeyDown = (e, i) => {
    const pos = enabled.indexOf(i);
    let next = null;
    if (e.key === "ArrowRight") next = enabled[(pos + 1) % enabled.length];
    else if (e.key === "ArrowLeft") next = enabled[(pos - 1 + enabled.length) % enabled.length];
    else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    if (next !== null && next !== undefined) {
      e.preventDefault();
      go(next);
    }
  };
  return (
    <div className={`pa-tabbar ${className}`} role="tablist" aria-label={ariaLabel}>
      {items.map((it, i) => {
        const on = it.key === value;
        return (
          <button
            key={it.key}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${it.key}`}
            aria-controls={`${idPrefix}-panel-${it.key}`}
            aria-selected={on}
            tabIndex={i === tabStop ? 0 : -1}
            disabled={it.disabled}
            className={`pa-tabbar-item ${on ? "is-active" : ""}`}
            onClick={() => onChange(it.key)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {it.icon && (
              <span className="pa-tabbar-icon" aria-hidden="true">
                {it.icon}
              </span>
            )}
            {it.label}
            {it.count !== undefined && it.count !== null && <span className="pa-tab-count">{count(it.count)}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Renders children only for the active tab (keepMounted hides instead, so
// grids and form state survive tab switches).
export const TabPanel = ({ idPrefix = "pa-tabs", tab, value, keepMounted = false, children, className = "" }) => {
  const active = tab === value;
  if (!active && !keepMounted) return null;
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${tab}`}
      aria-labelledby={`${idPrefix}-tab-${tab}`}
      hidden={!active}
      className={`pa-tabpanel ${className}`}
    >
      {children}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Drawer                                                              */
/* ------------------------------------------------------------------ */

let drawerSeq = 0;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

// Elements Tab can reach: focusable, not taken out of the tab order
// (tabindex=-1, e.g. FileDrop's hidden file input) and rendered.
const tabbables = (root) => Array.from(root.querySelectorAll(FOCUSABLE)).filter((n) => n.tabIndex >= 0 && !n.disabled && n.offsetParent !== null);

// An Esc meant for a popup inside the drawer, not for the drawer itself:
// - a flatpickr calendar (DateRangeInput) is open, or focus is in one; flatpickr
//   listens on document and closes itself (calling preventDefault) on Esc;
// - a combobox / menu button that is still expanded.
// Kit controls that close their own popup on Esc (SearchSelect) stop the
// event before it reaches document.
function escBelongsToPopup(e) {
  const t = e.target && e.target.nodeType === 1 ? e.target : null;
  if (document.querySelector(".flatpickr-calendar.open")) return true;
  if (t && t.closest && t.closest(".flatpickr-calendar:not(.inline)")) return true;
  if (e.defaultPrevented && t && (t._flatpickr || (t.classList && t.classList.contains("flatpickr-input")))) return true;
  if (t && t.getAttribute && t.getAttribute("aria-expanded") === "true") return true;
  return false;
}

// Right-hand side sheet for details and quick edits. Portalled to <body>;
// Esc, the close button and the backdrop close it; focus is trapped inside
// while open and returns to the opener afterwards. width: px or any CSS size
// (capped at the viewport). footer: action buttons, right aligned.
export function Drawer({ open, onClose, title, subtitle, width = 560, footer, children, closeOnBackdrop = true, className = "" }) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [titleId] = useState(() => `pa-drawer-title-${++drawerSeq}`);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = setTimeout(() => panelRef.current && panelRef.current.focus(), 0);
    const onKey = (e) => {
      // A dialog opened from inside the drawer handles its own Esc.
      if (document.querySelector(".modal.show")) return;
      if (e.key === "Escape") {
        if (escBelongsToPopup(e)) return;
        if (onCloseRef.current) onCloseRef.current();
      } else if (e.key === "Tab" && panelRef.current) {
        const nodes = tabbables(panelRef.current);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (opener && opener.focus) opener.focus();
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;
  return ReactDOM.createPortal(
    <div className="pa pa-drawer-root">
      <div className="pa-drawer-backdrop" onClick={closeOnBackdrop ? onClose : undefined} aria-hidden="true" />
      <div
        ref={panelRef}
        className={`pa-drawer ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ width: typeof width === "number" ? `${width}px` : width }}
      >
        <div className="pa-drawer-head">
          <div className="pa-drawer-titles">
            <h3 id={titleId}>{title}</h3>
            {subtitle && <div className="pa-drawer-sub">{subtitle}</div>}
          </div>
          <button type="button" className="pa-btn pa-btn-icon pa-btn-ghost" aria-label="Close" title="Close" onClick={onClose}>
            <IconX size={18} stroke={2} />
          </button>
        </div>
        <div className="pa-drawer-body">{children}</div>
        {footer && <div className="pa-drawer-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ */
/* Toolbar, Stack, FieldGrid                                           */
/* ------------------------------------------------------------------ */

// A row of controls above a list: left (search, filters, summary) and right
// (actions). Wraps on small screens. Children go to the left side.
export const Toolbar = ({ left, right, children, className = "", bordered = false }) => (
  <div className={`pa-toolbar ${bordered ? "pa-toolbar-bordered" : ""} ${className}`}>
    <div className="pa-toolbar-left">
      {left}
      {children}
    </div>
    {right && <div className="pa-toolbar-right">{right}</div>}
  </div>
);

const JUSTIFY = { start: "flex-start", end: "flex-end", center: "center", between: "space-between" };
const ALIGN = { start: "flex-start", end: "flex-end", center: "center", stretch: "stretch", baseline: "baseline" };

// Flex row/column with a gap on the 8px grid. gap in px (default 8).
export const Stack = ({ direction = "row", gap = 8, align, justify, wrap = direction === "row", className = "", style, children, as: Tag = "div" }) => (
  <Tag
    className={`pa-stack ${className}`}
    style={{
      display: "flex",
      flexDirection: direction,
      gap,
      alignItems: ALIGN[align] || (direction === "row" ? "center" : undefined),
      justifyContent: JUSTIFY[justify],
      flexWrap: wrap ? "wrap" : "nowrap",
      ...style,
    }}
  >
    {children}
  </Tag>
);

// Responsive grid for form fields / cards: as many columns of at least
// `min` px as fit (one column on phones).
export const FieldGrid = ({ min = 220, gap = 12, className = "", style, children }) => (
  <div className={`pa-fieldgrid ${className}`} style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))`, gap, ...style }}>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* useMediaQuery                                                       */
/* ------------------------------------------------------------------ */

// true while the CSS media query matches; follows viewport changes.
//   const wide = useMediaQuery("(min-width: 992px)");
// Use it for behaviour (pin grid columns only on wide screens); prefer CSS
// media queries for layout.
export function useMediaQuery(query) {
  const read = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}
